import React from 'react';
import i18n from '@/i18n/i18n';
import { STATUS_CONFIG } from './constants';
import { translatePhase } from '../../utils/fieldTranslations';

// Task Card Component
export function TaskCard({ task, onClick, formatDate, t }) {
  const isOverdue = task.is_overdue && task.status !== 'completed';
  const isReactivated = task.is_reactivated && task.reactivation_reason;

  return (
    <div
      onClick={onClick}
      className={`bg-slate-900/60 backdrop-blur-sm border rounded-xl p-4 cursor-pointer hover:border-slate-600 transition-all ${
        task.locked ? 'opacity-60' : ''
      } ${isOverdue ? 'border-rose-500/30 bg-rose-500/5' : ''} ${isReactivated && !isOverdue ? 'border-orange-500/30 bg-orange-500/5' : ''} ${!isOverdue && !isReactivated ? 'border-slate-800/60' : ''}`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-lg ${task.status === 'completed' ? 'text-emerald-400' : task.status === 'in_progress' ? 'text-cyan-400' : 'text-slate-500'}`}>
              {STATUS_CONFIG[task.status]?.icon || '○'}
            </span>
            <p className={`font-medium truncate ${task.locked ? 'text-slate-500' : 'text-slate-200'} ${task.status === 'completed' ? 'line-through text-slate-500' : ''}`}>
              {task.title}
            </p>
            {task.locked && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-400 flex-shrink-0" data-testid={`task-locked-${task.id}`}>
                {t('tasks.pro')}
              </span>
            )}
            {isReactivated && (
              <span className="text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30 flex-shrink-0" data-testid={`task-reactivated-${task.id}`}>
                {t('tasks.reactivated')}
              </span>
            )}
            {isOverdue && (
              <span className="text-xs px-2 py-0.5 rounded bg-rose-500/20 text-rose-400 border border-rose-500/30 flex-shrink-0">
                {t('tasks.overdue')}
              </span>
            )}
            {task.stricter_than_base && (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex-shrink-0" title={t('tasks.stricterThanIk')} data-testid={`task-stricter-${task.id}`}>
                ⚠ {t('tasks.stricter')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-400 ml-7">
            {task.deadline && <span>{t('tasks.deadline')}: {formatDate(task.deadline)}</span>}
            {task.responsible && <span>• {task.responsible}</span>}
            {task.category && (
              <span className="text-xs bg-slate-700/50 px-2 py-0.5 rounded text-slate-400">{task.category}</span>
            )}
            {task.phase && (
              <span className="text-xs bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded capitalize">{translatePhase(task.phase, i18n.language)}</span>
            )}
            {Array.isArray(task.covers_certs) && task.covers_certs.length > 1 && (
              <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20" title={t('tasks.multiCertCoverageTitle')} data-testid={`task-covers-${task.id}`}>
                {task.covers_certs.map(c => c.replace('ISO_', 'ISO ')).join(' + ')}
              </span>
            )}
            {task.gap_result_status && (
              <span
                className={`text-xs px-2 py-0.5 rounded border ${
                  task.gap_result_status === 'met' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                  task.gap_result_status === 'partial' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                  'bg-rose-500/10 text-rose-400 border-rose-500/30'
                }`}
                title={task.gap_result_summary || ''}
                data-testid={`task-ai-verdict-${task.id}`}
              >
                {task.gap_result_status === 'met' ? '✓' : task.gap_result_status === 'partial' ? '⚠' : '✗'}{' '}
                {t('tasks.aiVerdict')}: {t(`gap.${task.gap_result_status}`)} ({task.gap_result_confidence ?? 0}%)
              </span>
            )}
            {task._subtask_count > 0 && (
              <span
                className="text-xs bg-cyan-500/10 text-cyan-300 px-2 py-0.5 rounded border border-cyan-500/20"
                title={t('tasks.subtasksCount', { count: task._subtask_count })}
                data-testid={`task-subtasks-${task.id}`}
              >
                ↳ {task._subtask_completed}/{task._subtask_count} {t('tasks.subtasks')}
              </span>
            )}
          </div>
          {isReactivated && (
            <p className="text-xs text-orange-400 mt-1 ml-7 font-medium">
              {task.reactivation_reason}
            </p>
          )}
          {task.description && !isReactivated && (
            <p className="text-sm text-slate-500 mt-2 ml-7 line-clamp-2">{task.description.substring(0, 120)}...</p>
          )}
        </div>
        <svg className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// Task List Component
export function TaskList({ tasks, status, onTaskClick, formatDate, t }) {
  if (tasks.length === 0) {
    const emptyMessages = {
      not_started: t('tasks.noTasksNotStarted'),
      in_progress: t('tasks.noTasksInProgress'),
      completed: t('tasks.noTasksCompleted')
    };

    return (
      <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-12 text-center" data-testid="empty-task-list">
        <div className="text-4xl mb-3">{STATUS_CONFIG[status].icon}</div>
        <p className="text-slate-400">{emptyMessages[status]}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="task-list">
      {tasks.map(task => (
        <TaskCard
          key={task.id}
          task={task}
          onClick={() => onTaskClick(task)}
          formatDate={formatDate}
          t={t}
        />
      ))}
    </div>
  );
}
